/**
 * OptimierungDrawerShell — Panel-Chrome des Optimierungen-Drawers (Claude-Design drawer-v2):
 * Scrim + rechtes Panel, Kopf (Produkt + Schließen), Channel-Banner (Credits), Footer.
 */
import type { ReactNode } from 'react'
import { X, Zap } from 'lucide-react'

import { DV2 } from './tokens'

export interface OptimierungDrawerShellProps {
  productName: string
  channelName: string
  channelHost?: string
  channelLanguage?: string
  channelLogoText?: string
  channelLogoColor?: string
  creditBalance?: string
  channelSub: ReactNode
  footerMeta: ReactNode
  onClose: () => void
  children: ReactNode
}

export function OptimierungDrawerShell({
  productName,
  channelName,
  channelHost,
  channelLanguage,
  channelLogoText = 'a.',
  channelLogoColor = '#FF9900',
  creditBalance,
  channelSub,
  footerMeta,
  onClose,
  children,
}: OptimierungDrawerShellProps) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button type="button" aria-label="Schließen" className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative flex h-full w-[560px] max-w-[92vw] flex-col ${DV2.bg} ${DV2.fg2}`}>
        <div className={`flex items-center gap-3 border-b ${DV2.line} px-4 py-3`}>
          <div className="min-w-0 flex-1">
            <div className={`text-2xs font-semibold uppercase tracking-wide ${DV2.orange}`}>Optimierungen</div>
            <div className={`truncate text-sm-minus font-semibold ${DV2.fg1}`}>{productName}</div>
          </div>
          <button type="button" aria-label="Schließen" onClick={onClose} className={`rounded-md p-1.5 ${DV2.fg4} hover:${DV2.fg2}`}>
            <X size={14} />
          </button>
        </div>

        <div className={`flex items-center gap-3 border-b ${DV2.line} bg-[rgba(255,107,0,0.06)] px-4 py-3`}>
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm-minus font-bold text-black"
            style={{ background: channelLogoColor }}
          >
            {channelLogoText}
          </span>
          <div className="min-w-0 flex-1">
            <div className={`flex items-center gap-2 text-sm-minus font-semibold ${DV2.fg1}`}>
              {channelName}
              {(channelHost || channelLanguage) && (
                <span className={`font-mono text-2xs font-normal ${DV2.fg4}`}>
                  {[channelHost, channelLanguage].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>
            <div className={`mt-0.5 text-xs-plus ${DV2.fg3}`}>{channelSub}</div>
          </div>
          {creditBalance && (
            <div className={`flex items-center gap-1 font-mono text-xs-plus ${DV2.fg3}`}>
              <Zap size={10} className="text-primary-500" />
              <b className={DV2.fg2}>{creditBalance}</b> Cr
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">{children}</div>

        <div className={`flex items-center justify-between gap-3 border-t ${DV2.line} px-4 py-3`}>
          <div className={`min-w-0 text-xs-plus ${DV2.fg4}`}>{footerMeta}</div>
          <button type="button" onClick={onClose} className={`shrink-0 rounded-md border ${DV2.lineStrong} px-3 py-1.5 text-xs font-semibold ${DV2.fg2}`}>
            Schließen
          </button>
        </div>
      </div>
    </div>
  )
}
